// ===============================================================================
// Login.js - Login & Registration Page for SplitMon (OTP Registration)
// ===============================================================================
// Features: Two-step OTP registration, Password Strength Indicator
// ===============================================================================

import React, { useState, useEffect, useRef } from "react";
import { authAPI, settingsAPI, validatePassword } from "./api";
import "./Login.css";
import TermsModal from "./TermsModal";
import "./TermsModal.css";

function Login({ onLogin, initialRegisterMode = false, onBackToLanding, onForgotPassword }) {
  const [isRegister, setIsRegister] = useState(initialRegisterMode);
  
  // Login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  
  // Registration state
  const [regEmail, setRegEmail] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regName, setRegName] = useState("");
  
  // OTP state
  const [registrationStep, setRegistrationStep] = useState(1); // 1 = details, 2 = OTP
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [otpEmail, setOtpEmail] = useState(""); // Masked email for display
  const [otpExpiresIn, setOtpExpiresIn] = useState(10);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // OTP input refs
  const otpRefs = useRef([]);

  // Account Recovery state (for pending deletion)
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1); // 1 = prompt, 2 = OTP
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryUserId, setRecoveryUserId] = useState(null);
  const [recoveryDaysRemaining, setRecoveryDaysRemaining] = useState(0);
  const [recoveryDeletionDate, setRecoveryDeletionDate] = useState("");
  const [recoveryOtp, setRecoveryOtp] = useState(["", "", "", "", "", ""]);
  const [recoveryMaskedEmail, setRecoveryMaskedEmail] = useState("");
  const recoveryOtpRefs = useRef([]);

  // UI state
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  
  // Password strength
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [showPasswordHints, setShowPasswordHints] = useState(false);
 
  // Terms and Conditions
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // URL params (for messages)
  const [urlMessage, setUrlMessage] = useState(null);
 
  // Sync with initialRegisterMode prop
  useEffect(() => {
    setIsRegister(initialRegisterMode);
  }, [initialRegisterMode]);
 
  // Check URL params for messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('session_expired') === 'true') {
      setUrlMessage({ type: 'warning', text: 'Your session has expired. Please log in again.' });
    }
    
    if (params.toString()) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Password validation on change
  useEffect(() => {
    if (isRegister && regPassword) {
      setPasswordStrength(validatePassword(regPassword));
    }
  }, [regPassword, isRegister]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split("");
      const newOtp = [...otpCode];
      pastedCode.forEach((digit, i) => {
        if (i < 6 && /^\d$/.test(digit)) {
          newOtp[i] = digit;
        }
      });
      setOtpCode(newOtp);
      
      // Focus last filled or next empty
      const lastIndex = Math.min(pastedCode.length - 1, 5);
      otpRefs.current[lastIndex]?.focus();
      return;
    }
    
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");

    // Validate terms agreement
    if (!agreedToTerms) {
      setError("You must agree to the Terms and Conditions");
      return;
    }

    // Validate password
    const validation = validatePassword(regPassword);
    if (!validation.isValid) {
      setError("Password doesn't meet requirements");
      return;
    }

    setLoading(true);

    try {
      const res = await authAPI.registerSendOTP(regEmail, regUsername, regPassword, regName);
      
      if (res.data?.ok) {
        setOtpEmail(res.data.email);
        setOtpExpiresIn(res.data.expires_in_minutes || 10);
        setRegistrationStep(2);
        setResendCooldown(60); // 60 second cooldown for resend
        
        // Focus first OTP input
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      setError(err?.response?.data?.detail || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");

    const code = otpCode.join("");
    if (code.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setLoading(true);

    try {
      const res = await authAPI.registerVerifyOTP(regEmail, code);
      
      if (res.data?.ok) {
        const userData = res.data.user || {};
        
        setLoginStatus({
          user: userData,
          message: "Account Created!",
          subtitle: "Redirecting to dashboard...",
          type: "success"
        });
        
        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      setError(err?.response?.data?.detail || "Invalid verification code");
      setOtpCode(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setError("");
    setLoading(true);

    try {
      const res = await authAPI.resendOTP(regEmail);
      
      if (res.data?.ok) {
        setResendCooldown(60);
        setOtpCode(["", "", "", "", "", ""]);
        setError(""); // Clear any errors
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError(err?.response?.data?.detail || "Failed to resend code");
    } finally {
      setLoading(false);
    }
  };


// Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setUrlMessage(null);
    setLoading(true);

    try {
      const res = await authAPI.login(username, password);
      
      // Check for pending deletion FIRST (before ok check)
      if (res.data?.pending_deletion === true) {
        // Account is scheduled for deletion - show recovery option
        setRecoveryMode(true);
        setRecoveryStep(1);
        setRecoveryEmail(res.data.email);
        setRecoveryUserId(res.data.user_id);
        setRecoveryDaysRemaining(res.data.days_remaining);
        setRecoveryDeletionDate(res.data.deletion_scheduled_at);
        setLoading(false);
        return;
      }
      
      if (res.data?.ok) {
        const userData = res.data.user || {};
        
        setLoginStatus({
          user: userData,
          message: "Login Successful!",
          subtitle: "Redirecting to dashboard...",
          type: "success"
        });
        
        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorDetail = err?.response?.data?.detail;
      
      if (err?.response?.status === 423) {
        setError(errorDetail || "Account temporarily locked. Please try again later.");
      } else if (err?.response?.status === 429) {
        setError(errorDetail || "Too many attempts. Please try again later.");
      } else {
        setError(errorDetail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // Go back to step 1
  const handleBackToStep1 = () => {
    setRegistrationStep(1);
    setOtpCode(["", "", "", "", "", ""]);
    setError("");
  };

// =========================================================================
  // ACCOUNT RECOVERY HANDLERS
  // =========================================================================
  
  // Handle recovery OTP input change
  const handleRecoveryOtpChange = (index, value) => {
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split("");
      const newOtp = [...recoveryOtp];
      pastedCode.forEach((digit, i) => {
        if (i < 6 && /^\d$/.test(digit)) {
          newOtp[i] = digit;
        }
      });
      setRecoveryOtp(newOtp);
      const lastIndex = Math.min(pastedCode.length - 1, 5);
      recoveryOtpRefs.current[lastIndex]?.focus();
      return;
    }
    
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...recoveryOtp];
    newOtp[index] = value;
    setRecoveryOtp(newOtp);
    
    if (value && index < 5) {
      recoveryOtpRefs.current[index + 1]?.focus();
    }
  };

  // Handle recovery OTP backspace
  const handleRecoveryOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !recoveryOtp[index] && index > 0) {
      recoveryOtpRefs.current[index - 1]?.focus();
    }
  };

  // Send recovery OTP
  const handleSendRecoveryOTP = async () => {
    setError("");
    setLoading(true);

    try {
      const res = await settingsAPI.sendRecoveryOTP(recoveryEmail);
      
      if (res.data?.ok) {
        setRecoveryMaskedEmail(res.data.email);
        setRecoveryStep(2);
        setResendCooldown(60);
        setTimeout(() => recoveryOtpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      console.error("Send recovery OTP error:", err);
      setError(err?.response?.data?.detail || "Failed to send recovery code");
    } finally {
      setLoading(false);
    }
  };

  // Verify recovery OTP and restore account
  const handleVerifyRecoveryOTP = async (e) => {
    e.preventDefault();
    setError("");

    const code = recoveryOtp.join("");
    if (code.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setLoading(true);

    try {
      const res = await settingsAPI.verifyRecoveryOTP(recoveryEmail, code);
      
      if (res.data?.ok) {
        const userData = res.data.user || {};
        
        setLoginStatus({
          user: userData,
          message: "Account Recovered!",
          subtitle: "Your account has been restored. To delete again, go to Settings.",
          type: "success"
        });
        
        // Reset recovery state
        setRecoveryMode(false);
        setRecoveryStep(1);
        setRecoveryOtp(["", "", "", "", "", ""]);
        
        setTimeout(() => {
          onLogin(userData);
        }, 3000);
      }
    } catch (err) {
      console.error("Verify recovery OTP error:", err);
      setError(err?.response?.data?.detail || "Invalid recovery code");
      setRecoveryOtp(["", "", "", "", "", ""]);
      recoveryOtpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Cancel recovery and go back to login
  const handleCancelRecovery = () => {
    setRecoveryMode(false);
    setRecoveryStep(1);
    setRecoveryOtp(["", "", "", "", "", ""]);
    setRecoveryEmail("");
    setRecoveryUserId(null);
    setError("");
  };

  // Render loading/success state
  if (loading && loginStatus) {
    return (
      <div className="login-container">
        <div className="login-status-box">
          <div className={`status-icon ${loginStatus.type}`}>✓</div>
          <h3 className={`status-title ${loginStatus.type}`}>{loginStatus.message}</h3>
          <div className="status-user">
            <strong>Welcome, {loginStatus.user?.name || loginStatus.user?.username}!</strong>
          </div>
          <p className="status-subtitle">{loginStatus.subtitle}</p>
        </div>
      </div>
    );
  }

  if (loginStatus) {
    return (
      <div className="login-container">
        <div className="login-status-box">
          <div className={`status-icon ${loginStatus.type}`}>✓</div>
          <h3 className={`status-title ${loginStatus.type}`}>{loginStatus.message}</h3>
          <div className="status-user">
            <strong>Welcome, {loginStatus.user?.name || loginStatus.user?.username}!</strong>
          </div>
          <p className="status-subtitle">{loginStatus.subtitle}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Back to Landing Button */}
      {onBackToLanding && (
        <button className="back-to-landing" onClick={onBackToLanding}>
          ← Back to Home
        </button>
      )}

      <div className="login-box">
        <div className="login-form-section">
          <div className="login-form-content">
            <div className="login-logo">
              <span className="logo-icon">💰</span>
              <h1>SplitMon</h1>
              <p>Expense Monitoring</p>
            </div>

            {/* URL Messages */}
            {urlMessage && (
              <div className={`url-message ${urlMessage.type}`}>
                {urlMessage.text}
              </div>
            )}

{/* ============================================================= */}
            {/* ACCOUNT RECOVERY - STEP 1: Prompt */}
            {/* ============================================================= */}
            {recoveryMode && recoveryStep === 1 && (
              <>
                <h2 className="form-title">Account Recovery</h2>
                
                <div className="recovery-info">
                  <div className="recovery-warning">
                    <span className="warning-icon">Warning:</span>
                    <p>Your account is scheduled for deletion in <strong>{recoveryDaysRemaining} days</strong>.</p>
                  </div>
                  
                  <p className="recovery-message">
                    Would you like to recover your account? Click the button below to receive a verification code.
                  </p>
                  
                  <p className="recovery-note">
                    <strong>Note:</strong> If you want to delete your account again after recovery, go to Settings and repeat the deletion process.
                  </p>
                </div>

                <div className="recovery-actions">
                  <button 
                    onClick={handleSendRecoveryOTP}
                    disabled={loading}
                    className="login-button recovery-button"
                  >
                    {loading ? "Sending Code..." : "Recover My Account"}
                  </button>
                  
                  <button 
                    onClick={handleCancelRecovery}
                    disabled={loading}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>

                {error && <div className="login-error">{error}</div>}
              </>
            )}

            {/* ============================================================= */}
            {/* ACCOUNT RECOVERY - STEP 2: OTP Verification */}
            {/* ============================================================= */}
            {recoveryMode && recoveryStep === 2 && (
              <>
                <h2 className="form-title">Verify Recovery</h2>
                
                <p className="otp-subtitle">
                  Enter the 6-digit code sent to<br />
                  <strong>{recoveryMaskedEmail}</strong>
                </p>

                <form onSubmit={handleVerifyRecoveryOTP} className="login-form">
                  <div className="otp-inputs">
                    {recoveryOtp.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => recoveryOtpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleRecoveryOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleRecoveryOtpKeyDown(index, e)}
                        disabled={loading}
                        className="otp-input recovery-otp"
                        autoComplete="one-time-code"
                      />
                    ))}
                 </div>

                  {/* Terms and Conditions Checkbox */}
                  <div className={`terms-checkbox-container ${error && !agreedToTerms ? 'error' : ''}`}>
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      disabled={loading}
                    />
                    <label htmlFor="terms-checkbox">
                      I agree to the{" "}
                      <button 
                        type="button" 
                        onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                      >
                        Terms and Conditions
                      </button>
                    </label>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading || recoveryOtp.join("").length !== 6 || !agreedToTerms} 
                    className="login-button"
                  >
                    {loading ? "Recovering..." : "Recover Account"}
                  </button>
                </form>

                {error && <div className="login-error">{error}</div>}

                <div className="otp-actions">
                  <button 
                    type="button" 
                    onClick={handleSendRecoveryOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="resend-button"
                  >
                    {resendCooldown > 0 
                      ? `Resend code in ${resendCooldown}s` 
                      : "Resend Code"}
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={handleCancelRecovery}
                    className="back-button"
                  >
                    Cancel Recovery
                  </button>
                </div>
              </>
            )}

            {/* ============================================================= */}
            {/* LOGIN FORM */}
            {/* ============================================================= */}
            {!isRegister && !recoveryMode && (
              <>
                <h2 className="form-title">Welcome Back</h2>

                <form onSubmit={handleLogin} className="login-form">
                  <input
                    type="text"
                    placeholder="Username or Email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    required
                    className="login-input"
                    autoComplete="username email"
                  />

                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    className="login-input"
                    autoComplete="current-password"
                  />

                  <button type="submit" disabled={loading} className="login-button">
                    {loading ? "Signing In..." : "Sign In"}
                  </button>
                </form>

                {error && <div className="login-error">{error}</div>}

                <div className="forgot-password">
                  <button type="button" onClick={onForgotPassword} className="forgot-link">
                     Forgot your password?
                  </button>
                </div>

                <div className="login-switch">
                  Don't have an account?{" "}
                  <button 
                    type="button" 
                    onClick={() => { setIsRegister(true); setError(""); setUrlMessage(null); }}
                    className="switch-button"
                  >
                    Register
                  </button>
                </div>

                <div className="terms-link-footer">
                  <button type="button" onClick={() => setShowTermsModal(true)}>
                    Terms and Conditions
                  </button>
                </div>
              </>
            )}

            {/* ============================================================= */}
            {/* REGISTRATION STEP 1: Enter Details */}
            {/* ============================================================= */}
            {isRegister && registrationStep === 1 && (
              <>
                <h2 className="form-title">Create Account</h2>

                <form onSubmit={handleSendOTP} className="login-form">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    disabled={loading}
                    required
                    className="login-input"
                    autoComplete="name"
                  />
                  
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    disabled={loading}
                    required
                    className="login-input"
                    autoComplete="email"
                  />
                  
                  <input
                    type="text"
                    placeholder="Username"
                    value={regUsername}
                    onChange={(e) => setRegUsername(e.target.value)}
                    disabled={loading}
                    required
                    className="login-input"
                    autoComplete="username"
                  />

                  <div className="password-input-wrapper">
                    <input
                      type="password"
                      placeholder="Password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      onFocus={() => setShowPasswordHints(true)}
                      onBlur={() => setShowPasswordHints(false)}
                      disabled={loading}
                      required
                      className="login-input"
                      autoComplete="new-password"
                    />
                    
                    {/* Password Strength Indicator */}
                    {regPassword && (
                      <div className="password-strength">
                        <div className={`strength-bar ${passwordStrength.isValid ? 'strong' : regPassword.length > 4 ? 'medium' : 'weak'}`}>
                          <div className="strength-fill"></div>
                        </div>
                        <span className="strength-text">
                          {passwordStrength.isValid ? '✓ Strong' : regPassword.length > 4 ? 'Medium' : 'Weak'}
                        </span>
                      </div>
                    )}
                    
                    {/* Password Requirements */}
                    {showPasswordHints && !passwordStrength.isValid && (
                      <div className="password-hints">
                        <p>Password must have:</p>
                        <ul>
                          <li className={regPassword.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                          <li className={/[A-Z]/.test(regPassword) ? 'met' : ''}>One uppercase letter</li>
                          <li className={/[a-z]/.test(regPassword) ? 'met' : ''}>One lowercase letter</li>
                          <li className={/\d/.test(regPassword) ? 'met' : ''}>One number</li>
                          <li className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(regPassword) ? 'met' : ''}>One special character</li>
                        </ul>
                      </div>
                    )}
                 </div>

                  {/* Terms and Conditions Checkbox */}
                  <div className={`terms-checkbox-container ${error && !agreedToTerms ? 'error' : ''}`}>
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      disabled={loading}
                    />
                    <label htmlFor="terms-checkbox">
                      I agree to the{" "}
                      <button 
                        type="button" 
                        onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                      >
                        Terms and Conditions
                      </button>
                    </label>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading || !passwordStrength.isValid || !agreedToTerms} 
                    className="login-button"
                  >
                    {loading ? "Sending Code..." : "Send Verification Code"}
                  </button>
                </form>

                {error && <div className="login-error">{error}</div>}

                <div className="login-switch">
                  Already have an account?{" "}
                  <button 
                    type="button" 
                    onClick={() => { setIsRegister(false); setError(""); }}
                    className="switch-button"
                  >
                    Sign In
                  </button>
                </div>
              </>
            )}

            {/* ============================================================= */}
            {/* REGISTRATION STEP 2: Enter OTP */}
            {/* ============================================================= */}
            {isRegister && registrationStep === 2 && (
              <>
                <h2 className="form-title">Verify Your Email</h2>
                
                <p className="otp-subtitle">
                  We've sent a 6-digit code to<br />
                  <strong>{otpEmail}</strong>
                </p>

                <form onSubmit={handleVerifyOTP} className="login-form">
                  <div className="otp-inputs">
                    {otpCode.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => otpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        disabled={loading}
                        className="otp-input"
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  <p className="otp-expires">
                    Code expires in {otpExpiresIn} minutes
                  </p>

                  <button 
                    type="submit" 
                    disabled={loading || otpCode.join("").length !== 6} 
                    className="login-button"
                  >
                    {loading ? "Verifying..." : "Verify & Create Account"}
                  </button>
                </form>

                {error && <div className="login-error">{error}</div>}

                <div className="otp-actions">
                  <button 
                    type="button" 
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="resend-button"
                  >
                    {resendCooldown > 0 
                      ? `Resend code in ${resendCooldown}s` 
                      : "Resend Code"}
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={handleBackToStep1}
                    className="back-button"
                  >
                    ← Change Details
                  </button>
                </div>
              </>
            )}

          </div>
        </div>

        <div className="login-decor-section">
          <div className="decor-content">
            <h2>Split Expenses Easily</h2>
            <p>Monitor and track shared expenses with friends, roommates, and travel buddies</p>
            <div className="decor-features">
              <div className="feature-item">✓ Create groups</div>
              <div className="feature-item">✓ Add expenses</div>
              <div className="feature-item">✓ Monitor who owes who</div>
              <div className="feature-item">✓ Settle up</div>
            </div>
            
            {/* Security Badges */}
            <div className="security-badges">
              <div className="badge">🔒 Secure Login</div>
              <div className="badge">📧 Email Verified</div>
            </div>
          </div>
        </div>
     </div>

      {/* Terms and Conditions Modal */}
      <TermsModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
    </div>
  );
}

export default Login;
